import { useEffect, useRef } from 'react';
import { isStandalone } from '../platform/detector';
import { buildUrlFromPage } from './navigationUtils';

/**
 * Syncs navigation state with browser URL in standalone mode.
 * In Telegram mode, this hook does nothing.
 *
 * This enables:
 * - Browser URL changes when navigating (e.g., /app/team/123:abc)
 * - Browser back/forward button support
 * - Page refresh preserves current page
 * - Shareable URLs
 */
export const useUrlSync = (appLocation, setAppLocation, navigationStack) => {
  const isInternalNavigation = useRef(false);
  const lastPushedUrl = useRef(null);

  // Sync navigation stack changes to browser URL
  useEffect(() => {
    if (!isStandalone) return;

    const currentPage = navigationStack[navigationStack.length - 1];
    if (!currentPage) return;

    const url = '/app' + buildUrlFromPage(currentPage);

    // Avoid pushing if this change came from popstate (browser back/forward)
    if (isInternalNavigation.current) {
      isInternalNavigation.current = false;
      return;
    }

    // Avoid pushing duplicate URLs
    if (url === lastPushedUrl.current) return;

    const currentPath = window.location.pathname + window.location.search;

    if (currentPath !== url) {
      window.history.pushState({ appLocation }, '', url);
      lastPushedUrl.current = url;
    }
  }, [navigationStack, appLocation]);

  // Set up popstate listener and initial URL
  useEffect(() => {
    if (!isStandalone) return;

    // Listen for browser back/forward buttons
    const handlePopState = () => {
      isInternalNavigation.current = true;

      // Parse URL to get new location
      const pathname = window.location.pathname;
      const search = window.location.search;
      const newLocation = pathname.replace('/app', '') + search || '/home';

      setAppLocation(newLocation);
    };

    window.addEventListener('popstate', handlePopState);

    // Set initial URL on mount (replace, don't push)
    const currentPage = navigationStack[navigationStack.length - 1];
    if (currentPage) {
      const url = '/app' + buildUrlFromPage(currentPage);
      window.history.replaceState({ appLocation }, '', url);
      lastPushedUrl.current = url;
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAppLocation]); // Only run once on mount, navigationStack captured at init time
};
